import clsx from "clsx";
import { CSSProperties, useEffect, useId, useMemo, useRef, useState } from "react";
import downArrowImg from "../../../assets/images/icons/downArrow.svg";
import { useUserSizeCandidates } from "../../../hooks/useUserSizeCandidates";
import { SIZE_LIST } from "../../../pages/utils/helper";
import i18n from "../../../i18n";
import { useTranslation } from "react-i18next";

type SizeGroup = {
  key: string
  label: string
  sizes: string[]
}

type SizeCandidateInputProps = {
  label?: string
  labelClassName?: string
  outerClassName?: string
  fieldWrapperClassName?: string
  inputClassName?: string
  toggleButtonClassName?: string
  dropdownClassName?: string
  optionClassName?: string
  placeholder?: string
  value: string
  onChange: (value: string) => void
  readOnly?: boolean
  disabled?: boolean
  maxLength?: number
  width?: number | string
}

type SizeCandidateSelectProps = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  fieldWrapperClassName?: string
  buttonClassName?: string
  dropdownClassName?: string
  optionClassName?: string
}

const FIXED_SIZE_GROUPS: SizeGroup[] = Object.entries(SIZE_LIST).map(([groupKey, groupData]) => ({
  key: groupKey,
  label: i18n.t(groupData.labelKey),
  sizes: groupData.size
}))

const normalizeSizeCompareKey = (value: string): string => value.trim().toUpperCase()

const FIXED_SIZE_SET = new Set(
  FIXED_SIZE_GROUPS.flatMap((group) => group.sizes.map((size) => normalizeSizeCompareKey(size)))
)

const buildSizeGroups = (candidates: string[]): SizeGroup[] => {
  const seenHistory = new Set<string>()
  const historySizes = candidates.flatMap((candidate) => {
    const trimmedCandidate = candidate.trim()
    const compareKey = normalizeSizeCompareKey(trimmedCandidate)
    if (trimmedCandidate === "" || FIXED_SIZE_SET.has(compareKey) || seenHistory.has(compareKey)) {
      return []
    }
    seenHistory.add(compareKey)
    return [trimmedCandidate]
  })

  const groups = FIXED_SIZE_GROUPS.map((group) => ({
    ...group,
    label: i18n.t(SIZE_LIST[group.key as keyof typeof SIZE_LIST].labelKey)
  }))
  if (historySizes.length > 0) {
    groups.unshift({
      key: "history",
      label: i18n.t("common.history"),
      sizes: historySizes
    })
  }

  return groups
}

function SizeCandidateDropdown({
  groups,
  listboxId,
  selectedValue,
  onSelect,
  dropdownClassName,
  optionClassName
}: {
  groups: SizeGroup[]
  listboxId: string
  selectedValue: string
  onSelect: (size: string) => void
  dropdownClassName?: string
  optionClassName?: string
}) {
  return (
    <div
      id={listboxId}
      className={clsx("absolute left-0 right-0 top-[40px] z-20 max-h-[220px] overflow-y-auto rounded border border-[#D9D9D9] bg-white p-2 shadow", dropdownClassName)}
    >
      {groups.map((group) => (
        <div key={group.key} className="mb-2 last:mb-0">
          <p className="text-xs font-bold text-[#666666]">{group.label}</p>
          <div
            className="mt-1 space-y-1"
            role="listbox"
            aria-label={group.label}
          >
            {group.sizes.map((size) => (
              <button
                key={`${group.key}-${size}`}
                type="button"
                role="option"
                aria-selected={selectedValue === size}
                className={clsx("block w-full rounded px-2 py-1 text-left text-sm hover:bg-[#F3F4F6]", optionClassName)}
                onClick={() => onSelect(size)}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function useDropdownState(disabled: boolean) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node)) {
        return
      }

      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("pointerdown", handlePointerDown)
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
    }
  }, [isOpen])

  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false)
    }
  }, [disabled, isOpen])

  return {
    isOpen,
    setIsOpen,
    containerRef
  }
}

function resolveWidthStyle(width?: number | string): CSSProperties | undefined {
  if (width === undefined) {
    return undefined
  }
  if (typeof width === "number") {
    return { width: `${width}px` }
  }
  return { width }
}

export function SizeCandidateInput({
  label,
  labelClassName,
  outerClassName,
  fieldWrapperClassName,
  inputClassName,
  toggleButtonClassName,
  dropdownClassName,
  optionClassName,
  placeholder,
  value,
  onChange,
  readOnly = false,
  disabled = false,
  maxLength,
  width
}: SizeCandidateInputProps) {
  const { candidates } = useUserSizeCandidates()
  const { i18n: translationI18n } = useTranslation()
  const groups = useMemo(() => buildSizeGroups(candidates), [candidates, translationI18n.language])
  const { isOpen, setIsOpen, containerRef } = useDropdownState(disabled || readOnly)
  const widthStyle = resolveWidthStyle(width)
  const listboxId = useId()
  const isToggleDisabled = disabled || readOnly

  const field = (
    <div
      className={clsx("relative", fieldWrapperClassName)}
      ref={containerRef}
      style={!label ? widthStyle : undefined}
    >
      <input
        type="text"
        placeholder={placeholder ?? ""}
        className={clsx(
          "w-full h-[33px] rounded-[8px] border border-[#d9d9d9] bg-white px-3 pr-10 text-[12px] shadow-[inset_0px_2px_2px_0px_rgba(0,0,0,0.1),0px_2px_2px_0px_rgba(255,255,255,0.15)]",
          inputClassName
        )}
        value={value}
        maxLength={maxLength}
        onChange={(event) => {
          if (disabled || readOnly) {
            return
          }
          onChange(event.target.value)
        }}
        readOnly={readOnly}
        disabled={disabled}
        aria-readonly={readOnly}
      />
      <button
        type="button"
        className={clsx(
          "absolute right-0 top-0 flex h-full w-9 items-center justify-center rounded-r-[8px] border-l border-[#D9D9D9] bg-white",
          isToggleDisabled && "cursor-auto",
          toggleButtonClassName
        )}
        disabled={isToggleDisabled}
        onClick={() => {
          if (isToggleDisabled) {
            return
          }
          setIsOpen((prev) => !prev)
        }}
        aria-label={i18n.t("accessibility.showSizeCandidates")}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? listboxId : undefined}
        aria-disabled={isToggleDisabled}
      >
        <img src={downArrowImg} alt="" className="h-[6px] w-[11px]" />
      </button>
      {isOpen && (
        <SizeCandidateDropdown
          groups={groups}
          listboxId={listboxId}
          selectedValue={value}
          dropdownClassName={dropdownClassName}
          optionClassName={optionClassName}
          onSelect={(size) => {
            onChange(size)
            setIsOpen(false)
          }}
        />
      )}
    </div>
  )

  if (!label) {
    return field
  }

  return (
    <label className={clsx("w-full", outerClassName)} style={widthStyle}>
      <span className={clsx("mb-[4px] block text-sm text-gray-600", labelClassName)}>{label}</span>
      {field}
    </label>
  )
}

export function SizeCandidateSelect({
  value,
  onChange,
  placeholder = "--",
  disabled = false,
  fieldWrapperClassName,
  buttonClassName,
  dropdownClassName,
  optionClassName
}: SizeCandidateSelectProps) {
  const { candidates } = useUserSizeCandidates()
  const { i18n: translationI18n } = useTranslation()
  const groups = useMemo(() => buildSizeGroups(candidates), [candidates, translationI18n.language])
  const { isOpen, setIsOpen, containerRef } = useDropdownState(disabled)
  const listboxId = useId()

  return (
    <div className={clsx("relative", fieldWrapperClassName)} ref={containerRef}>
      <button
        type="button"
        className={clsx(
          "flex w-full items-center justify-between rounded border border-[#D1D5DB] bg-white px-2 py-1 text-left shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]",
          disabled && "cursor-not-allowed bg-[#F3F4F6]",
          buttonClassName
        )}
        disabled={disabled}
        onClick={() => {
          if (disabled) {
            return
          }
          setIsOpen((prev) => !prev)
        }}
        onPointerDown={(event) => {
          event.stopPropagation()
        }}
        aria-label={i18n.t("accessibility.showSizeCandidates")}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? listboxId : undefined}
        aria-disabled={disabled}
      >
        <span>{value || placeholder}</span>
        <img
          src={downArrowImg}
          alt=""
          aria-hidden="true"
          className="h-[10px] w-[10px]"
        />
      </button>
      {isOpen && (
        <div
          onPointerDown={(event) => {
            event.stopPropagation()
          }}
        >
          <SizeCandidateDropdown
            groups={groups}
            listboxId={listboxId}
            selectedValue={value}
            dropdownClassName={clsx("top-[38px]", dropdownClassName)}
            optionClassName={optionClassName}
            onSelect={(size) => {
              onChange(size)
              setIsOpen(false)
            }}
          />
        </div>
      )}
    </div>
  )
}
